import type { ReactNode } from "react";
import { Modal } from "./Modal";

export type ConfirmTone = "danger" | "caution";

export interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /**
   * danger — red destructive (delete). caution — yellow primary
   * (remove assignment, disconnect, etc.).
   */
  tone?: ConfirmTone;
  busy?: boolean;
  onConfirm: () => void;
}

/**
 * Two-step confirmation using the same portal shell as {@link Modal}.
 * Footer order is Cancel then Confirm so the first tab stop is the safe
 * action (Cancel), matching destructive-dialog UX expectations.
 */
export function ConfirmModal({
  open,
  onClose,
  title,
  children,
  confirmLabel = "Delete",
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
}: ConfirmModalProps) {
  const confirmClass =
    tone === "caution" ? "btn btn-primary" : "btn btn-danger";

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      busy={busy}
      footer={
        <>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={busy}
            autoFocus
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            className={confirmClass}
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? "Please wait…" : confirmLabel}
          </button>
        </>
      }
    >
      <div
        style={{
          fontSize: "0.95rem",
          lineHeight: 1.55,
          color: "var(--portal-text)",
        }}
      >
        {children}
      </div>
    </Modal>
  );
}
